/**
 * A compact label/value grid for the facts a card states about something — a
 * host's heartbeat and checkout, an action's preconditions and failure advice.
 *
 * One shared shape rather than ad-hoc caption lines: the operator reads these
 * cards about once a week, so the eye should learn a single place to find "what
 * is this card telling me" and a single place to find "what does that mean".
 * The label column is sized to its longest label so values line up across rows.
 */
import { Fragment, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

export interface Fact {
    label: string;
    value: ReactNode;
}

export function FactList({ facts }: { facts: Fact[] }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'max-content minmax(0, 1fr)',
                columnGap: 2,
                rowGap: 0.75,
                alignItems: 'baseline',
            }}
        >
            {facts.map((f) => (
                <Fragment key={f.label}>
                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        {f.label}
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ minWidth: 0 }}>
                        {f.value}
                    </Typography>
                </Fragment>
            ))}
        </Box>
    );
}
